import { ChangeEventHandler, KeyboardEventHandler, useState } from 'react';

interface Props<T> {
  data: T[];
  movePageCallback: (index: number) => void;
}

export const PAGE_SIZE = 15;

export const usePagination = <T>({ data, movePageCallback }: Props<T>) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const pageCount = Math.ceil(data.length / PAGE_SIZE);
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < pageCount - 1;
  const currentData = data.slice(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE);

  const isValidPage = (value: number) => {
    return value && value > 0 && value <= pageCount;
  };

  const handleChangeCurrentPage: ChangeEventHandler<HTMLInputElement> = (e) => {
    const { value } = e.currentTarget;
    if (isNaN(Number(value))) {
      return;
    }
    setCurrentPage(Math.round(Number(value)));
  };

  const handleMovePage = (page: number) => {
    const roundedPage = Math.round(page);
    if (isValidPage(roundedPage)) {
      setCurrentPage(roundedPage);
      setPageIndex(roundedPage - 1);
      movePageCallback(roundedPage - 1);
    } else {
      // reset to current page when invalid page
      setCurrentPage(pageIndex + 1);
    }
  };

  const handleKeyUpPage: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') {
      handleMovePage(currentPage);
    }
  };

  return {
    pageIndex,
    pageCount,
    currentPage,
    canPreviousPage,
    canNextPage,
    currentData,
    handleMovePage,
    handleKeyUpPage,
    handleChangeCurrentPage,
  };
};
